import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ref, update, remove } from "firebase/database";
import { useObjectVal } from "react-firebase-hooks/database";
import {
  Table,
  Tabs,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  message,
  Spin,
  Tag,
  Switch,
  Space,
  Typography,
} from "antd";
import {
  PlusCircle,
  PencilSimple,
  Trash,
  SignOut,
  ArrowLeft,
} from "@phosphor-icons/react";
import styled from "styled-components";
import { useAuth } from "../contexts/auth";
import { FirebaseService } from "../utility";

const { TextArea } = Input;
const { Title } = Typography;

const ADMIN_EMAILS = [
  "george@techmaven.us",
  "thedubaicashflowclub@gmail.com",
];

// --- Styled Components ---

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #1a2332;
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav::before {
    border-bottom-color: #2c3e50;
  }
  .ant-tabs-tab {
    color: #a1a1aa !important;
    font-size: 15px;
    &:hover {
      color: #fff !important;
    }
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #fff !important;
  }
  .ant-tabs-ink-bar {
    background: #78c8b8;
  }
`;

const StyledTable = styled(Table)`
  .ant-table {
    background: #223043;
    border-radius: 8px;
    color: #e4e4e7;
  }
  .ant-table-thead > tr > th {
    background: #1a2332 !important;
    color: #a1a1aa !important;
    border-bottom: 1px solid #2c3e50;
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #2c3e50;
    color: #e4e4e7;
  }
  .ant-table-tbody > tr:hover > td {
    background: #2c3e50 !important;
  }
  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: #2c3e50 !important;
  }
  .ant-pagination {
    .ant-pagination-item {
      background: #223043;
      border-color: #2c3e50;
      a {
        color: #e4e4e7;
      }
      &-active {
        border-color: #78c8b8;
        a {
          color: #78c8b8;
        }
      }
    }
    .ant-pagination-prev,
    .ant-pagination-next {
      .ant-pagination-item-link {
        background: #223043;
        border-color: #2c3e50;
        color: #e4e4e7;
      }
    }
  }
  .ant-empty-description {
    color: #a1a1aa;
  }
`;

const ActionButton = styled(Button)`
  &.ant-btn-text {
    color: #a1a1aa;
    &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.08);
    }
  }
`;

const currency = (v) =>
  v != null ? `$${Number(v).toLocaleString()}` : "";

// --- Field definitions per card category ---

const makeBigDealFields = (assetTypes = []) => [
  { name: "name", label: "Name", required: true },
  { name: "type", label: "Type", required: true, input: "select", options: ["Real Estate", "Limited Partnership", "Property Damage", "Automated Business"] },
  { name: "description", label: "Description", input: "textarea" },
  { name: "rule", label: "Rule", input: "textarea", tooltip: "Instructions shown to the player, e.g. sell conditions, ROI info, or price range." },
  { name: "tag", label: "Tag", tooltip: "Short identifier shown on the player's financial statement (e.g. 8PLEX, DUPLEX, LP-Auto)." },
  { name: "cost", label: "Cost", input: "number", tooltip: "Total purchase price of the asset." },
  { name: "downPayment", label: "Down Payment", input: "number", tooltip: "Cash the player must pay upfront to acquire this deal. Mortgage is auto-calculated as Cost minus Down Payment." },
  { name: "cashFlow", label: "Cash Flow", input: "number", tooltip: "Monthly income (or loss if negative) added to the player's passive income." },
  { name: "roi", label: "ROI", input: "number", step: 0.01, tooltip: "Return on investment as a decimal (e.g. 0.6 = 60%). Calculated as Cash Flow x 12 / Down Payment." },
  { name: "landType", label: "Land Type (Asset Type)", input: "select", options: assetTypes, tooltip: "Choose from the standardized Asset Types list. Must match the Type on Market Offer cards for this asset to be sellable." },
  { name: "units", label: "Units", input: "number", tooltip: "Number of rental units. Used by plex/apartment offers that pay per unit." },
  { name: "propertyType", label: "Property Type (damage cards)", tooltip: "Which property type this damage card affects (e.g. 8-plex, rental). Only for Property Damage cards." },
];

const makeSmallDealFields = (assetTypes = []) => [
  { name: "name", label: "Name", required: true },
  { name: "type", label: "Type", required: true, input: "select", options: ["Stock", "Mutual Fund", "Real Estate", "Certificate of Deposit", "Company", "Coin", "Preferred Stock"] },
  { name: "description", label: "Description", input: "textarea" },
  { name: "rule", label: "Rule", input: "textarea", tooltip: "Instructions shown to the player, e.g. who can buy/sell and at what price." },
  { name: "symbol", label: "Symbol", tooltip: "Ticker symbol for stocks/funds (e.g. MYT4U, GRO4US). Used to group same-stock cards together." },
  { name: "tag", label: "Tag", tooltip: "Short identifier shown on the player's financial statement." },
  { name: "price", label: "Price", input: "number", tooltip: "Price per share/unit for stocks, mutual funds, and coins." },
  { name: "cost", label: "Cost", input: "number", tooltip: "Total cost for real estate or company deals." },
  { name: "downPayment", label: "Down Payment", input: "number", tooltip: "Cash the player must pay upfront. Mortgage is auto-calculated as Cost minus Down Payment." },
  { name: "cashFlow", label: "Cash Flow", input: "number", tooltip: "Monthly passive income added to the player." },
  { name: "roi", label: "ROI", input: "number", step: 0.01, tooltip: "Return on investment as a decimal (e.g. 0.45 = 45%)." },
  { name: "dividend", label: "Dividend", input: "number", tooltip: "Per-share dividend paid to the player each time a dividend card is drawn for this stock." },
  { name: "range", label: "Price Range", tooltip: "Historic trading range shown to the player (e.g. '$1 to $30'). Helps them decide whether to buy." },
  { name: "landType", label: "Land Type (Asset Type)", input: "select", options: assetTypes, tooltip: "Choose from the standardized Asset Types list. Must match the Type on Market Offer cards for this asset to be sellable." },
  { name: "businessType", label: "Business Type", tooltip: "Sub-type for Company cards (e.g. software, widget). Used to match with related offer cards." },
  { name: "stockSplit", label: "Stock Split", input: "switch", tooltip: "If enabled, this card doubles the player's shares of this stock instead of offering a buy/sell." },
  { name: "reverseSplit", label: "Reverse Split", input: "switch", tooltip: "If enabled, this card halves the player's shares of this stock." },
  { name: "splitRatio", label: "Split Ratio", tooltip: "The split multiplier (e.g. '2:1' for stock split, '1:2' for reverse split)." },
];

const doodadFields = [
  { name: "name", label: "Name", required: true },
  { name: "text", label: "Text", input: "textarea", required: true, tooltip: "Message displayed to the player when they draw this card." },
  { name: "cost", label: "Cost", input: "number", tooltip: "Fixed dollar amount deducted from the player's cash." },
  { name: "amount", label: "Amount (fraction of savings)", input: "number", step: 0.01, tooltip: "Takes a fraction of the player's savings instead of a fixed cost. E.g. 0.5 = 50% of savings. Leave empty if using fixed cost." },
  { name: "loan", label: "Loan", input: "number", tooltip: "Loan balance added to the player's liabilities (e.g. $17,000 boat loan)." },
  { name: "payment", label: "Monthly Payment", input: "number", tooltip: "Recurring monthly expense added to the player's financial statement alongside the loan." },
  { name: "key", label: "Liability Key", tooltip: "Links this doodad to a player liability. Only used for doodads that create ongoing monthly payments (e.g. boatLoan, creditCardPayment). Leave empty for one-time costs." },
  { name: "debt", label: "Debt Added", input: "number", tooltip: "Increases an existing liability balance (e.g. adds $4,000 to credit card debt for a Big Screen TV purchase)." },
  { name: "child", label: "Requires Children", input: "switch", tooltip: "If enabled, this card only applies to players who have children." },
  { name: "onlyOne", label: "Only One Allowed", input: "switch", tooltip: "If enabled, a player can only have one of this doodad (e.g. one boat loan)." },
];

const makeOfferFields = (assetTypes = []) => [
  { name: "name", label: "Name", required: true },
  { name: "type", label: "Asset Type", required: true, input: "select", options: assetTypes, tooltip: "Choose from the standardized Asset Types list. Must exactly match the Land Type on the deal cards this offer can buy." },
  { name: "description", label: "Description", input: "textarea" },
  { name: "rule1", label: "Rule 1", input: "textarea", tooltip: "Who can sell — shown to all players (e.g. 'Everyone may sell at this price')." },
  { name: "rule2", label: "Rule 2", input: "textarea", tooltip: "What happens when you sell — mortgage payoff, cash flow loss, etc." },
  { name: "offer", label: "Offer Price", input: "number", tooltip: "Fixed total price offered for the property. Used for houses, condos, and specific assets. Leave empty if using per-unit pricing." },
  { name: "offerPerUnit", label: "Offer Per Unit", input: "number", tooltip: "Price per unit for plex/apartment offers. Multiplied by the number of units the player owns. Leave empty if using fixed offer price." },
  { name: "cashFlow", label: "Cash Flow Change", input: "number", tooltip: "Change to monthly cash flow when this offer triggers (e.g. +400 for business improvement, -500 for brother-in-law deal)." },
  { name: "lowestUnit", label: "Min Units Required", input: "number", tooltip: "Minimum number of units the player's property must have to qualify (e.g. 12 for large apartment buyers). Set to 0 or leave empty for no minimum." },
];

const dreamFields = [
  { name: "text", label: "Dream Title", required: true, tooltip: "Short goal shown on the dream card (e.g. 'YACHT RACING')." },
  { name: "description", label: "Description", input: "textarea", required: true, tooltip: "Full description shown when the player selects this dream." },
  { name: "cost", label: "Dream Cost", input: "number", required: true, tooltip: "How much passive income the player needs to earn above their starting Fast Track income to win (e.g. 50000)." },
];

const scenarioFields = [
  { name: "jobTitle", label: "Job Title", required: true, tooltip: "Display name shown on the player's financial statement (e.g. 'Doctor (MD)'). Also used as the scenario key." },
  { name: "startingSalary", label: "Starting Salary", input: "number", required: true, tooltip: "Monthly salary the player starts with." },
  { name: "startingSavings", label: "Starting Savings", input: "number", required: true, tooltip: "Cash the player starts with." },
  { name: "taxes", label: "Taxes", input: "number", tooltip: "Monthly tax expense." },
  { name: "mortgagePayment", label: "Mortgage Payment", input: "number", tooltip: "Monthly home mortgage payment." },
  { name: "mortgage", label: "Mortgage Balance", input: "number", tooltip: "Starting mortgage liability balance." },
  { name: "carPayment", label: "Car Payment", input: "number", tooltip: "Monthly car loan payment." },
  { name: "carLoan", label: "Car Loan Balance", input: "number", tooltip: "Starting car loan liability balance." },
  { name: "creditCardPayment", label: "Credit Card Payment", input: "number", tooltip: "Monthly credit card payment." },
  { name: "creditDebt", label: "Credit Card Debt", input: "number", tooltip: "Starting credit card liability balance." },
  { name: "retailPayment", label: "Retail Payment", input: "number", tooltip: "Monthly retail debt payment." },
  { name: "retailDebt", label: "Retail Debt", input: "number", tooltip: "Starting retail liability balance." },
  { name: "otherExpenses", label: "Other Expenses", input: "number", tooltip: "Monthly miscellaneous expenses." },
  { name: "children", label: "Children Expense", input: "number", tooltip: "Monthly expense added per child (e.g. 200 means each child costs $200/mo)." },
  { name: "loans", label: "School Loan Payment", input: "number", tooltip: "Monthly school/student loan payment." },
  { name: "schoolLoan", label: "School Loan Balance", input: "number", tooltip: "Starting school loan liability balance." },
];

// --- Column definitions per category ---

const bigDealColumns = [
  { title: "Key", dataIndex: "key", width: 180, ellipsis: true, sorter: (a, b) => a.key.localeCompare(b.key) },
  { title: "Name", dataIndex: "name", width: 200, ellipsis: true },
  { title: "Type", dataIndex: "type", width: 160, render: (v) => <Tag color="blue">{v}</Tag> },
  { title: "Cost", dataIndex: "cost", width: 120, render: currency, sorter: (a, b) => (a.cost || 0) - (b.cost || 0) },
  { title: "Down Payment", dataIndex: "downPayment", width: 130, render: currency },
  { title: "Cash Flow", dataIndex: "cashFlow", width: 110, render: (v) => v != null ? <span style={{ color: v >= 0 ? "#8CB369" : "#E15554" }}>{currency(v)}</span> : "" },
  { title: "ROI", dataIndex: "roi", width: 80, render: (v) => v != null ? `${(v * 100).toFixed(0)}%` : "" },
];

const smallDealColumns = [
  { title: "Key", dataIndex: "key", width: 180, ellipsis: true, sorter: (a, b) => a.key.localeCompare(b.key) },
  { title: "Name", dataIndex: "name", width: 200, ellipsis: true },
  { title: "Type", dataIndex: "type", width: 160, render: (v) => <Tag color="purple">{v}</Tag> },
  { title: "Symbol", dataIndex: "symbol", width: 100 },
  { title: "Price", dataIndex: "price", width: 100, render: currency },
  { title: "Cost", dataIndex: "cost", width: 100, render: currency },
  { title: "Cash Flow", dataIndex: "cashFlow", width: 110, render: (v) => v != null ? <span style={{ color: v >= 0 ? "#8CB369" : "#E15554" }}>{currency(v)}</span> : "" },
];

const doodadColumns = [
  { title: "Key", dataIndex: "key", width: 150, ellipsis: true, sorter: (a, b) => a.key.localeCompare(b.key) },
  { title: "Name", dataIndex: "name", width: 220, ellipsis: true },
  { title: "Cost", dataIndex: "cost", width: 100, render: currency, sorter: (a, b) => (a.cost || 0) - (b.cost || 0) },
  { title: "Amount", dataIndex: "amount", width: 100, render: (v) => v != null ? `${v * 100}%` : "" },
  { title: "Loan", dataIndex: "loan", width: 100, render: currency },
  { title: "Children", dataIndex: "child", width: 90, render: (v) => v ? <Tag color="cyan">Yes</Tag> : "" },
];

const offerColumns = [
  { title: "Key", dataIndex: "key", width: 150, ellipsis: true, sorter: (a, b) => a.key.localeCompare(b.key) },
  { title: "Name", dataIndex: "name", width: 220, ellipsis: true },
  { title: "Type", dataIndex: "type", width: 140, render: (v) => <Tag color="orange">{v}</Tag> },
  { title: "Offer", dataIndex: "offer", width: 130, render: currency },
  { title: "Per Unit", dataIndex: "offerPerUnit", width: 120, render: currency },
  { title: "Cash Flow", dataIndex: "cashFlow", width: 110, render: (v) => v != null ? <span style={{ color: v >= 0 ? "#8CB369" : "#E15554" }}>{currency(v)}</span> : "" },
];

const dreamColumns = [
  { title: "#", dataIndex: "index", width: 60 },
  { title: "Title", dataIndex: "text", width: 280, ellipsis: true },
  { title: "Cost", dataIndex: "cost", width: 120, render: currency },
  { title: "Description", dataIndex: "description", ellipsis: true },
];

const scenarioColumns = [
  { title: "Job Title", dataIndex: "key", width: 200, ellipsis: true, sorter: (a, b) => a.key.localeCompare(b.key) },
  { title: "Salary", dataIndex: "startingSalary", width: 120, render: currency, sorter: (a, b) => (a.startingSalary || 0) - (b.startingSalary || 0) },
  { title: "Savings", dataIndex: "startingSavings", width: 110, render: currency },
  { title: "Taxes", dataIndex: "taxes", width: 100, render: currency },
  { title: "Mortgage Pmt", dataIndex: "mortgagePayment", width: 130, render: currency },
  { title: "Other Exp.", dataIndex: "otherExpenses", width: 110, render: currency },
  { title: "Children Exp.", dataIndex: "children", width: 120, render: currency },
  { title: "School Loan Pmt", dataIndex: "loans", width: 140, render: currency },
  { title: "School Loan Bal", dataIndex: "schoolLoan", width: 140, render: currency },
];

// --- Card Editor Modal ---

function CardEditorModal({ open, onClose, onSave, card, fields, title }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (card) {
        form.setFieldsValue(card);
      }
    }
  }, [open, card, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      // Clean undefined/null values
      const cleaned = {};
      for (const [k, v] of Object.entries(values)) {
        if (v !== undefined && v !== null && v !== "") {
          cleaned[k] = v;
        }
      }
      onSave(cleaned);
    } catch {
      // validation failed
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText={card ? "Update" : "Create"}
      width={640}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 8 }}>
        {fields.map((f) => {
          if (f.input === "textarea") {
            return (
              <Form.Item key={f.name} name={f.name} label={f.label} tooltip={f.tooltip} rules={f.required ? [{ required: true, message: `${f.label} is required` }] : undefined}>
                <TextArea rows={3} />
              </Form.Item>
            );
          }
          if (f.input === "number") {
            return (
              <Form.Item key={f.name} name={f.name} label={f.label} tooltip={f.tooltip} rules={f.required ? [{ required: true, message: `${f.label} is required` }] : undefined}>
                <InputNumber step={f.step || 1} style={{ width: "100%" }} />
              </Form.Item>
            );
          }
          if (f.input === "switch") {
            return (
              <Form.Item key={f.name} name={f.name} label={f.label} tooltip={f.tooltip} valuePropName="checked">
                <Switch />
              </Form.Item>
            );
          }
          if (f.input === "select") {
            const baseOptions = (f.options || []).map((o) => ({ label: o, value: o }));
            const currentValue = card?.[f.name];
            const hasCurrent = currentValue && !f.options?.includes(currentValue);
            const opts = hasCurrent
              ? [...baseOptions, { label: `${currentValue} (legacy — not in list)`, value: currentValue }]
              : baseOptions;
            return (
              <Form.Item key={f.name} name={f.name} label={f.label} tooltip={f.tooltip} rules={f.required ? [{ required: true, message: `${f.label} is required` }] : undefined}>
                <Select options={opts} showSearch allowClear />
              </Form.Item>
            );
          }
          return (
            <Form.Item key={f.name} name={f.name} label={f.label} tooltip={f.tooltip} rules={f.required ? [{ required: true, message: `${f.label} is required` }] : undefined}>
              <Input />
            </Form.Item>
          );
        })}
      </Form>
    </Modal>
  );
}

// --- Card Table Section ---

function CardSection({ category, basePath, columns, fields, data, keyPromptText }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [localData, setLocalData] = useState(data);
  const [messageApi, contextHolder] = message.useMessage();

  const dbBasePath = basePath || `cards/${category}`;

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const dataSource = useMemo(() => {
    if (!localData) return [];
    return Object.entries(localData).map(([key, val]) => ({ ...val, key }));
  }, [localData]);

  const handleAdd = () => {
    setEditingCard(null);
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingCard(record);
    setModalOpen(true);
  };

  const handleDelete = async (record) => {
    try {
      await remove(ref(FirebaseService.database, `${dbBasePath}/${record.key}`));
      setLocalData((prev) => {
        const updated = { ...prev };
        delete updated[record.key];
        return updated;
      });
      messageApi.success("Deleted");
    } catch (err) {
      messageApi.error("Failed to delete: " + err.message);
    }
  };

  const handleSave = async (values) => {
    try {
      if (editingCard) {
        await update(ref(FirebaseService.database, `${dbBasePath}/${editingCard.key}`), values);
        setLocalData((prev) => ({ ...prev, [editingCard.key]: values }));
        messageApi.success("Updated");
      } else {
        const promptText = keyPromptText || "Enter a unique key for this card (e.g., realEstateB36, doodad50, offer42):";
        const newKey = window.prompt(promptText);
        if (!newKey || !newKey.trim()) return;
        const cleanKey = newKey.trim();
        if (localData && localData[cleanKey]) {
          messageApi.error("A record with this key already exists");
          return;
        }
        await update(ref(FirebaseService.database, `${dbBasePath}/${cleanKey}`), values);
        setLocalData((prev) => ({ ...prev, [cleanKey]: values }));
        messageApi.success("Created");
      }
      setModalOpen(false);
    } catch (err) {
      messageApi.error("Failed to save: " + err.message);
    }
  };

  const allColumns = [
    ...columns,
    {
      title: "Actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <ActionButton type="text" icon={<PencilSimple size={18} />} onClick={() => handleEdit(record)} />
          <Popconfirm title="Delete this record?" onConfirm={() => handleDelete(record)} okText="Delete" okButtonProps={{ danger: true }}>
            <ActionButton type="text" icon={<Trash size={18} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: "#a1a1aa", fontSize: 14 }}>{dataSource.length} records</span>
        <Button type="primary" icon={<PlusCircle size={18} />} onClick={handleAdd} style={{ background: "#78C8B8", borderColor: "#78C8B8", color: "#1a2332" }}>
          Add
        </Button>
      </div>
      <StyledTable
        columns={allColumns}
        dataSource={dataSource}
        childrenColumnName="__treeData__"
        pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: [10, 15, 25, 50] }}
        scroll={{ x: "max-content" }}
        size="middle"
      />
      <CardEditorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        card={editingCard}
        fields={fields}
        title={editingCard ? `Edit: ${editingCard.name || editingCard.key}` : "Add New"}
      />
    </>
  );
}

// --- Dreams Section (array-based) ---

function DreamsSection({ data }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [localData, setLocalData] = useState(data);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const dataSource = useMemo(() => {
    if (!localData) return [];
    if (Array.isArray(localData)) {
      return localData.map((d, i) => ({ key: String(i), index: i, ...d }));
    }
    return Object.entries(localData)
      .map(([k, v]) => ({ key: k, index: Number(k), ...v }))
      .sort((a, b) => a.index - b.index);
  }, [localData]);

  const handleAdd = () => { setEditingItem(null); setModalOpen(true); };
  const handleEdit = (record) => { setEditingItem(record); setModalOpen(true); };

  const handleDelete = async (record) => {
    try {
      await remove(ref(FirebaseService.database, `dreams/${record.index}`));
      setLocalData((prev) => {
        const updated = { ...prev };
        delete updated[record.index];
        return updated;
      });
      messageApi.success("Dream deleted");
    } catch (err) {
      messageApi.error("Failed to delete: " + err.message);
    }
  };

  const handleSave = async (values) => {
    try {
      if (editingItem !== null) {
        await update(ref(FirebaseService.database, `dreams/${editingItem.index}`), values);
        setLocalData((prev) => ({ ...prev, [editingItem.index]: values }));
        messageApi.success("Dream updated");
      } else {
        const nextIndex = dataSource.length > 0
          ? Math.max(...dataSource.map((d) => d.index)) + 1
          : 0;
        await update(ref(FirebaseService.database, `dreams/${nextIndex}`), values);
        setLocalData((prev) => ({ ...prev, [nextIndex]: values }));
        messageApi.success("Dream added");
      }
      setModalOpen(false);
    } catch (err) {
      messageApi.error("Failed to save: " + err.message);
    }
  };

  const columns = [
    ...dreamColumns,
    {
      title: "Actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <ActionButton type="text" icon={<PencilSimple size={18} />} onClick={() => handleEdit(record)} />
          <Popconfirm title="Delete this dream?" onConfirm={() => handleDelete(record)} okText="Delete" okButtonProps={{ danger: true }}>
            <ActionButton type="text" icon={<Trash size={18} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: "#a1a1aa", fontSize: 14 }}>{dataSource.length} dreams</span>
        <Button type="primary" icon={<PlusCircle size={18} />} onClick={handleAdd} style={{ background: "#78C8B8", borderColor: "#78C8B8", color: "#1a2332" }}>
          Add Dream
        </Button>
      </div>
      <StyledTable
        columns={columns}
        dataSource={dataSource}
        pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: [10, 15, 25, 50] }}
        scroll={{ x: "max-content" }}
        size="middle"
      />
      <CardEditorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        card={editingItem}
        fields={dreamFields}
        title={editingItem ? `Edit: ${editingItem.text}` : "Add New Dream"}
      />
    </>
  );
}

// --- Asset Types Section ---

function AssetTypesSection({ data }) {
  const [editing, setEditing] = useState(null);
  const [draftValue, setDraftValue] = useState("");
  const [adding, setAdding] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const list = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) {
      return data.map((value, index) => ({ index, value }));
    }
    return Object.entries(data)
      .map(([k, v]) => ({ index: Number(k), value: v }))
      .sort((a, b) => a.index - b.index);
  }, [data]);

  const writeAll = async (next) => {
    const payload = {};
    next.forEach((value, i) => {
      payload[i] = value;
    });
    await update(ref(FirebaseService.database, "/"), { assetTypes: payload });
  };

  const startAdd = () => {
    setAdding(true);
    setDraftValue("");
    setEditing(null);
  };

  const startEdit = (record) => {
    setEditing(record.index);
    setDraftValue(record.value);
    setAdding(false);
  };

  const cancel = () => {
    setEditing(null);
    setAdding(false);
    setDraftValue("");
  };

  const save = async () => {
    const trimmed = draftValue.trim();
    if (!trimmed) {
      messageApi.error("Asset type cannot be empty");
      return;
    }
    const others = list.filter((r) => r.index !== editing).map((r) => r.value);
    if (others.includes(trimmed)) {
      messageApi.error("That asset type already exists");
      return;
    }
    try {
      let next;
      if (adding) {
        next = [...list.map((r) => r.value), trimmed];
      } else {
        next = list.map((r) => (r.index === editing ? trimmed : r.value));
      }
      await writeAll(next);
      messageApi.success("Saved");
      cancel();
    } catch (err) {
      messageApi.error("Failed to save: " + err.message);
    }
  };

  const handleDelete = async (record) => {
    try {
      const next = list.filter((r) => r.index !== record.index).map((r) => r.value);
      await writeAll(next);
      messageApi.success("Deleted");
    } catch (err) {
      messageApi.error("Failed to delete: " + err.message);
    }
  };

  const columns = [
    { title: "#", dataIndex: "index", width: 60 },
    {
      title: "Asset Type Name",
      dataIndex: "value",
      render: (_, record) =>
        editing === record.index ? (
          <Input
            autoFocus
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            onPressEnter={save}
          />
        ) : (
          record.value
        ),
    },
    {
      title: "Actions",
      width: 160,
      fixed: "right",
      render: (_, record) =>
        editing === record.index ? (
          <Space>
            <Button type="primary" size="small" onClick={save}>Save</Button>
            <Button size="small" onClick={cancel}>Cancel</Button>
          </Space>
        ) : (
          <Space>
            <ActionButton type="text" icon={<PencilSimple size={18} />} onClick={() => startEdit(record)} />
            <Popconfirm title="Delete this asset type?" onConfirm={() => handleDelete(record)} okText="Delete" okButtonProps={{ danger: true }}>
              <ActionButton type="text" icon={<Trash size={18} />} />
            </Popconfirm>
          </Space>
        ),
    },
  ];

  return (
    <>
      {contextHolder}
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: "#a1a1aa", fontSize: 14 }}>
          {list.length} asset type{list.length === 1 ? "" : "s"} — used as the dropdown options for Big/Small Deal "Land Type" and Market Offer "Asset Type". Match between deals and offers must be exact.
        </span>
        <Button type="primary" icon={<PlusCircle size={18} />} onClick={startAdd} style={{ background: "#78C8B8", borderColor: "#78C8B8", color: "#1a2332" }}>
          Add Asset Type
        </Button>
      </div>
      {adding && (
        <div style={{ marginBottom: 16, display: "flex", gap: 8 }}>
          <Input
            autoFocus
            placeholder="e.g. 8-Plex"
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            onPressEnter={save}
            style={{ maxWidth: 400 }}
          />
          <Button type="primary" onClick={save}>Save</Button>
          <Button onClick={cancel}>Cancel</Button>
        </div>
      )}
      <StyledTable
        columns={columns}
        dataSource={list.map((r) => ({ ...r, key: r.index }))}
        pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: [10, 25, 50, 100] }}
        size="middle"
      />
    </>
  );
}

// --- Main Admin Screen ---

export function AdminScreen() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [cardsData, cardsLoading] = useObjectVal(ref(FirebaseService.database, "cards"));
  const [dreamsData, dreamsLoading] = useObjectVal(ref(FirebaseService.database, "dreams"));
  const [scenariosData, scenariosLoading] = useObjectVal(ref(FirebaseService.database, "scenarios"));
  const [assetTypesData, assetTypesLoading] = useObjectVal(ref(FirebaseService.database, "assetTypes"));

  const assetTypeOptions = useMemo(() => {
    if (!assetTypesData) return [];
    if (Array.isArray(assetTypesData)) return assetTypesData.filter(Boolean);
    return Object.entries(assetTypesData)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([, v]) => v)
      .filter(Boolean);
  }, [assetTypesData]);

  const bigDealFields = useMemo(() => makeBigDealFields(assetTypeOptions), [assetTypeOptions]);
  const smallDealFields = useMemo(() => makeSmallDealFields(assetTypeOptions), [assetTypeOptions]);
  const offerFields = useMemo(() => makeOfferFields(assetTypeOptions), [assetTypeOptions]);

  // Admin gate
  if (!isAuthenticated || !user) {
    return (
      <PageWrapper style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <Title level={3} style={{ color: "#fff" }}>Please log in first</Title>
          <Button type="primary" onClick={() => navigate("/login")}>Go to Login</Button>
        </div>
      </PageWrapper>
    );
  }

  if (!ADMIN_EMAILS.includes(user.email)) {
    return (
      <PageWrapper style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <Title level={3} style={{ color: "#E15554" }}>Access Denied</Title>
          <p style={{ color: "#a1a1aa" }}>You are not authorized to access this page.</p>
          <Button onClick={() => navigate("/rooms")}>Back to Game</Button>
        </div>
      </PageWrapper>
    );
  }

  if (cardsLoading || dreamsLoading || scenariosLoading || assetTypesLoading) {
    return (
      <PageWrapper style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </PageWrapper>
    );
  }

  const dreamsCount = dreamsData
    ? Array.isArray(dreamsData) ? dreamsData.length : Object.keys(dreamsData).length
    : 0;

  const tabs = [
    {
      key: "bigDeal",
      label: `Big Deals (${cardsData?.bigDeal ? Object.keys(cardsData.bigDeal).length : 0})`,
      children: <CardSection category="bigDeal" columns={bigDealColumns} fields={bigDealFields} data={cardsData?.bigDeal} />,
    },
    {
      key: "smallDeal",
      label: `Small Deals (${cardsData?.smallDeal ? Object.keys(cardsData.smallDeal).length : 0})`,
      children: <CardSection category="smallDeal" columns={smallDealColumns} fields={smallDealFields} data={cardsData?.smallDeal} />,
    },
    {
      key: "doodad",
      label: `Doodads (${cardsData?.doodad ? Object.keys(cardsData.doodad).length : 0})`,
      children: <CardSection category="doodad" columns={doodadColumns} fields={doodadFields} data={cardsData?.doodad} />,
    },
    {
      key: "offer",
      label: `Market Offers (${cardsData?.offer ? Object.keys(cardsData.offer).length : 0})`,
      children: <CardSection category="offer" columns={offerColumns} fields={offerFields} data={cardsData?.offer} />,
    },
    {
      key: "dreams",
      label: `Dreams (${dreamsCount})`,
      children: <DreamsSection data={dreamsData} />,
    },
    {
      key: "scenarios",
      label: `Scenarios (${scenariosData ? Object.keys(scenariosData).length : 0})`,
      children: (
        <CardSection
          basePath="scenarios"
          columns={scenarioColumns}
          fields={scenarioFields}
          data={scenariosData}
          keyPromptText="Enter the job title as the key (e.g. 'Doctor (MD)', 'Janitor'):"
        />
      ),
    },
    {
      key: "assetTypes",
      label: `Asset Types (${assetTypeOptions.length})`,
      children: <AssetTypesSection data={assetTypesData} />,
    },
  ];

  return (
    <PageWrapper>
      <Header>
        <HeaderLeft>
          <Button
            type="text"
            icon={<ArrowLeft size={20} />}
            onClick={() => navigate("/rooms")}
            style={{ color: "#a1a1aa" }}
          />
          <Title level={3} style={{ color: "#fff", margin: 0 }}>
            Card Manager
          </Title>
        </HeaderLeft>
        <Space>
          <span style={{ color: "#a1a1aa", fontSize: 13 }}>{user.email}</span>
          <Button type="text" icon={<SignOut size={20} />} onClick={logout} style={{ color: "#a1a1aa" }} />
        </Space>
      </Header>
      <StyledTabs items={tabs} size="large" />
    </PageWrapper>
  );
}
